
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { useAppContext } from '../AppContext';
import { PageContext } from '../PageContext';
import { Page } from '../types';

const LoginPage: React.FC = () => {
    const { login, resetPassword } = useAuth();
    const { appConfig } = useAppContext();
    const pageContext = useContext(PageContext);
    
    const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isResettingPassword, setIsResettingPassword] = useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    const switchMethod = (method: 'email' | 'phone') => {
        setLoginMethod(method);
        setError('');
        setMessage('');
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            await login(email, password);
        } catch (err: any) {
            switch (err.code) {
                case 'auth/user-not-found':
                case 'auth/wrong-password':
                case 'auth/invalid-credential':
                    setError('Invalid email or password.');
                    break;
                case 'auth/invalid-email':
                    setError('Please enter a valid email address.');
                    break;
                default:
                    setError('An unexpected error occurred. Please try again.');
                    break;
            }
        } finally {
            setIsLoading(false);
        }
    };
    
    const handlePhoneLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+1${phoneNumber}`;
            const synthesizedEmail = `${formattedPhone.replace(/\+/g, '')}@phone.whatwasit.app`;
            await login(synthesizedEmail, password);
        } catch (err: any) {
            console.error(err);
            switch (err.code) {
                case 'auth/user-not-found':
                case 'auth/wrong-password':
                case 'auth/invalid-credential':
                    setError('Invalid phone number or password.');
                    break;
                case 'auth/invalid-email':
                    setError('Please enter a valid phone number.');
                    break;
                default:
                    setError(err.message || 'An unexpected error occurred. Please try again.');
                    break;
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setIsLoading(true);
        
        if (!email) {
            setError('Please enter your email address.');
            setIsLoading(false);
            return;
        }

        try {
            await resetPassword(email);
            setMessage('Password reset email sent! Check your inbox.');
        } catch (err: any) {
            switch (err.code) {
                case 'auth/user-not-found':
                    setError('No user found with this email.');
                    break;
                case 'auth/invalid-email':
                    setError('Please enter a valid email address.');
                    break;
                default:
                    setError('Failed to send reset email. Please try again.');
                    break;
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-bg min-h-[100dvh] w-full flex flex-col items-center p-4 pb-12 relative overflow-y-auto">
            {/* Styles & Animations */}
            <style>{`
                .snowflake {
                    position: absolute;
                    top: -10px;
                    background: white;
                    border-radius: 50%;
                    opacity: 0.6;
                    animation: fall linear infinite;
                    pointer-events: none;
                }
                @keyframes fall {
                    to { transform: translateY(110vh); }
                }
                /* 3D Coin Spin Animation */
                @keyframes spin-y {
                    0% { transform: rotateY(0deg); }
                    100% { transform: rotateY(360deg); }
                }
                .logo-spin-3d {
                    animation: spin-y 6s linear infinite;
                    transform-style: preserve-3d;
                }
                .perspective-container {
                    perspective: 1000px;
                }
                /* Keyframes for form animation */
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .form-slide-up {
                    animation: slideUp 0.8s ease-out forwards;
                }
            `}</style>

            {/* Generated Snowflakes */}
            {Array.from({ length: 40 }).map((_, i) => (
                <div
                    key={i}
                    className="snowflake"
                    style={{
                        left: `${Math.random() * 100}%`,
                        width: `${Math.random() * 4 + 2}px`,
                        height: `${Math.random() * 4 + 2}px`,
                        animationDuration: `${Math.random() * 5 + 5}s`,
                        animationDelay: `-${Math.random() * 5}s`
                    }}
                />
            ))}

            <div className="text-center text-white max-w-sm w-full z-10 perspective-container my-auto">
                {/* Company Logo */}
                {appConfig.logoUrl && (
                    <img 
                        src={appConfig.logoUrl} 
                        alt="Company Logo" 
                        className="w-48 mx-auto mb-4 object-contain drop-shadow-2xl logo-spin-3d" 
                    />
                )}
                
                <h1 className="text-4xl sm:text-5xl font-extrabold mb-1" style={{ fontFamily: "'Caveat', cursive" }}>
                    <span className="aurora-text shimmering-text">What Was It?</span>
                </h1>
                <p className="text-base text-gray-300 mb-4">
                    {isResettingPassword ? "Reset your password" : "Share moments, solve mysteries, and challenge your family and friends."}
                </p>

                {!isResettingPassword && (
                    <div className="flex justify-center mb-4 space-x-4">
                        <button
                            type="button"
                            onClick={() => switchMethod('email')}
                            className={`px-4 py-2 rounded-full font-bold transition-colors ${loginMethod === 'email' ? 'bg-pink-500 text-white' : 'bg-black/20 text-gray-300 hover:bg-black/40'}`}
                        >
                            Email
                        </button>
                        <button
                            type="button"
                            onClick={() => switchMethod('phone')}
                            className={`px-4 py-2 rounded-full font-bold transition-colors ${loginMethod === 'phone' ? 'bg-pink-500 text-white' : 'bg-black/20 text-gray-300 hover:bg-black/40'}`}
                        >
                            Phone
                        </button>
                    </div>
                )}

                {!isResettingPassword ? (
                    loginMethod === 'email' ? (
                    <form onSubmit={handleLogin} className="space-y-4 form-slide-up">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email Address"
                            className="w-full bg-white text-gray-900 placeholder-gray-500 py-3 px-4 rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                            aria-label="Email Address"
                            required
                        />
                         <div className="relative w-full">
                            <input
                                type={isPasswordVisible ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password"
                                className="w-full bg-white text-gray-900 placeholder-gray-500 py-3 px-4 rounded-lg shadow-lg pr-10 focus:outline-none focus:ring-2 focus:ring-pink-500"
                                aria-label="Password"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                            >
                                <i className={`ph-duotone ${isPasswordVisible ? 'ph-eye-slash' : 'ph-eye'} text-xl`}></i>
                            </button>
                        </div>
                        
                        <div className="text-right">
                            <button 
                                type="button"
                                onClick={() => { setIsResettingPassword(true); setError(''); setMessage(''); }}
                                className="text-sm text-pink-400 hover:underline font-semibold"
                            >
                                Forgot Password?
                            </button>
                        </div>

                        {error && <p className="text-red-400 text-sm font-bold bg-red-900/20 p-2 rounded">{error}</p>}
                        
                        <button
                            type="submit"
                            disabled={isLoading || !email || !password}
                            className="w-full bg-white text-purple-700 font-bold py-3 px-6 rounded-lg shadow-lg transform transition-transform hover:scale-105 disabled:bg-gray-300 disabled:scale-100 flex items-center justify-center"
                        >
                            {isLoading ? <div className="spinner-white" style={{ borderColor: 'rgba(107, 33, 168, 0.5)', borderLeftColor: 'var(--primary-dark-purple)'}}></div> : 'Sign In'}
                        </button>
                    </form>
                    ) : (
                    <div className="space-y-4 form-slide-up">
                            <form onSubmit={handlePhoneLogin} className="space-y-4">
                                <input
                                    type="tel"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    placeholder="Phone Number (e.g. +1...)"
                                    className="w-full bg-white text-gray-900 placeholder-gray-500 py-3 px-4 rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                                    aria-label="Phone Number"
                                    required
                                />
                                <div className="relative w-full">
                                    <input
                                        type={isPasswordVisible ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Password"
                                        className="w-full bg-white text-gray-900 placeholder-gray-500 py-3 px-4 rounded-lg shadow-lg pr-10 focus:outline-none focus:ring-2 focus:ring-pink-500"
                                        aria-label="Password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                        aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                                    >
                                        <i className={`ph-duotone ${isPasswordVisible ? 'ph-eye-slash' : 'ph-eye'} text-xl`}></i>
                                    </button>
                                </div>
                                {error && <p className="text-red-400 text-sm font-bold bg-red-900/20 p-2 rounded">{error}</p>}
                                <button
                                    type="submit"
                                    disabled={isLoading || !phoneNumber || !password}
                                    className="w-full bg-white text-purple-700 font-bold py-3 px-6 rounded-lg shadow-lg transform transition-transform hover:scale-105 disabled:bg-gray-300 disabled:scale-100 flex items-center justify-center"
                                >
                                    {isLoading ? <div className="spinner-white" style={{ borderColor: 'rgba(107, 33, 168, 0.5)', borderLeftColor: 'var(--primary-dark-purple)'}}></div> : 'Sign In'}
                                </button>
                            </form>
                    </div>
                    )
                ) : (
                    <form onSubmit={handleResetPassword} className="space-y-4 form-slide-up">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email address"
                            className="w-full bg-white text-gray-900 placeholder-gray-500 py-3 px-4 rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                            aria-label="Email Address"
                            required
                        />
                        
                        {error && <p className="text-red-400 text-sm font-bold bg-red-900/20 p-2 rounded">{error}</p>}
                        {message && <p className="text-green-400 text-sm font-bold bg-green-900/20 p-2 rounded">{message}</p>}

                        <button
                            type="submit"
                            disabled={isLoading || !email}
                            className="w-full bg-[var(--accent-pink)] text-white font-bold py-3 px-6 rounded-lg shadow-lg transform transition-transform hover:scale-105 disabled:bg-gray-500 disabled:scale-100 flex items-center justify-center"
                        >
                            {isLoading ? <div className="spinner-white"></div> : 'Send Reset Link'}
                        </button>
                        
                        <button
                            type="button"
                            onClick={() => { setIsResettingPassword(false); setError(''); setMessage(''); }}
                            className="w-full bg-transparent border border-white/30 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:bg-white/10"
                        >
                            Back to Login
                        </button>
                    </form>
                )}

                {!isResettingPassword && (
                    <p className="text-sm text-gray-400 mt-6 form-slide-up" style={{ animationDelay: '0.7s' }}>
                        Don't have an account?{' '}
                        <button
                            onClick={() => pageContext?.switchPage(Page.Signup, 'Sign Up')}
                            className="font-bold text-pink-400 hover:underline"
                        >
                            Sign Up
                        </button>
                    </p>
                )}
            </div>
        </div>
    );
};

export default LoginPage;
